import Button from '@components/ui/AddButton'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'

import 'swiper/css/navigation'
import SwiperCore, { Navigation } from 'swiper'
import Link from 'next/link'
import {
  BTN_ADD_TO_FAVORITES,
  GENERAL_ENGRAVING,
  IMG_PLACEHOLDER,
} from '@components/utils/textVariables'
import { stringToBoolean } from '@framework/utils/parse-util'
import { generateUri } from '@commerce/utils/uri-util'
import AttributesHandler from '../ProductView/AttributesHandler'
import { priceFormat } from '@framework/utils/parse-util'
import Spinner from '@components/ui/Spinner'
import { removePrecedingSlash, removeSucceedingSlug } from '@framework/utils/app-util'
import { Cross, Play } from '@components/icons'
import { useProductQuickViewHook } from './hooks/productQuickView.hook'
import { round } from '@framework/utils/lodash'
import dynamic from 'next/dynamic'
import { IMAGE_ASSET_BASE_URL } from '@framework/utils/constants'

const ReactPlayerWrapper = dynamic(
  () => import('@components/product/ProductView/ReactPlayerWrapper'),
  { ssr: false }
)

declare const window: any
declare const AF: any

SwiperCore.use([Navigation])

export default function QuickViewModal({
  quickViewProductSlug,
  isQuickview,
  setQuickview,
  productData,
  collectionName,
  deviceInfo,
  isFromCart,
  setLoader,
  title,
  page,
}: any) {
  const css = { maxWidth: '100%', height: 'auto' }

  const {
    quickViewData,
    reviewData,
    selectedAttrData,
    currentVariantData,
    currentPage,
    isInWishList,
    isEngravingAvailable,
    buttonConfig,
    product,
    selectedSizeVariant,
    showEngravingModal,
    closeModalAll,
    handleWishList,
    handleViewItemGAEvent,
    attributeChanged,
    setSelectedAttrData,
    handleGAEventExitPopupView,
  } = useProductQuickViewHook({
    deviceInfo,
    collectionName,
    setLoader,
    isFromCart,
    quickViewProductSlug,
    productData,
    setQuickview,
    AF,
    window,
    title,
    page,
  })

  const selectedSizeProduct = selectedSizeVariant ?? quickViewData
  const selectedSizeProducSaving =
    selectedSizeProduct?.listPrice?.raw?.withTax -
    selectedSizeProduct?.price?.raw?.withTax
  const selectedSizeProductDiscount = round(
    (selectedSizeProducSaving / selectedSizeProduct?.listPrice?.raw?.withTax) *
      100,
    0
  )

  return (
    <>
      {/* QUICK VIEW PANEL FROM BOTTOM */}
      <Transition.Root show={quickViewData != undefined} as={Fragment}>
        <Dialog
          as="div"
          className="relative quick-view z-9999"
          onClose={() => {
            handleGAEventExitPopupView()
            setQuickview(undefined)
          }}
        >
          <div className="fixed inset-0 left-0 bg-black/[.5]" />
          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="fixed inset-y-0 right-0 flex max-w-full pointer-events-none pt-52 small-pt-52">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-400"
                  enterFrom="translate-y-full"
                  enterTo="translate-y-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-400"
                  leaveFrom="translate-y-0"
                  leaveTo="translate-y-full"
                >
                  <Dialog.Panel className="w-screen pointer-events-auto">
                    <div className="relative z-50 flex flex-col h-full bg-white shadow-xl">
                      <div className="sticky top-0 right-0 z-10 px-6 pt-6 sm:px-6">
                        <div className="flex justify-between">
                          <div>
                            <div className="flex flex-col sm:hidden">
                              <h2 className="mb-1 font-normal text-12 text-brown-light">
                                {quickViewData?.classification?.category}
                              </h2>
                              <h1 className="grid grid-cols-12 mb-2 text-lg font-bold tracking-tight text-black truncate text-ellipsis sm:text-2xl sm:grid-cols-6">
                                <div className="col-span-8 sm:col-span-4 mob-truncate">
                                  {quickViewData?.name || quickViewData?.name}{' '}
                                </div>
                              </h1>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="mr-2 text-black rounded-md outline-none hover:text-gray-500"
                            onClick={() => {
                              handleGAEventExitPopupView()
                              setQuickview(undefined)
                            }}
                          >
                            <span className="sr-only">Close panel</span>
                            <Cross
                              className="relative w-5 h-5 sm:w-8 sm:h-8 -top-3"
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      </div>

                      {quickViewData?.link ? (
                        !quickViewData ? (
                          <>
                            <div className="flex flex-col justify-center py-48 text-center">
                              <Spinner />
                            </div>
                          </>
                        ) : (
                          <>
                            {/* Main */}
                            <div className="px-0 pb-0 overflow-y-scroll sm:px-6">
                              <div className="grid grid-cols-1 mx-auto sm:grid-cols-12 sm:gap-10 sm:px-24 ipd-px-24">
                                <div className="mb-4 sm:col-span-7 sm:mb-0 sm:pl-0 ipd-col-7">
                                  <div className="sticky top-0 grid grid-cols-1 gap-x-1 gap-y-1 home-banner-swiper m-hide-navigation">
                                    <Swiper
                                      slidesPerView={1.8}
                                      spaceBetween={6}
                                      navigation={true}
                                      slidesOffsetBefore={24}
                                      loop={false}
                                      breakpoints={{
                                        640: {
                                          slidesPerView: 1.8,
                                        },
                                        768: {
                                          slidesPerView: 1,
                                        },
                                        1024: {
                                          slidesPerView: 2,
                                        },
                                      }}
                                    >
                                      <div
                                        role="list"
                                        className="inline-flex mx-4 space-x-0 sm:mx-0 lg:mx-0 lg:space-x-0 lg:grid lg:grid-cols-4 lg:gap-x-0"
                                      >
                                        {quickViewData?.images
                                          ?.slice(0, 1)
                                          ?.map(
                                            (image: any, idx: number) =>
                                              image?.tag != 'specification' && (
                                                <SwiperSlide
                                                  className="px-0"
                                                  key={`${idx}-slider`}
                                                >
                                                  <div
                                                    key={idx}
                                                    className="inline-flex flex-col w-full text-center cursor-pointer lg:w-auto h-full"
                                                  >
                                                    <div className="relative group long-image h-full">
                                                      {image?.image ? (
                                                        <div className="image-container h-full">
                                                          <Link
                                                            legacyBehavior
                                                            href={`/${removeSucceedingSlug(quickViewData?.link)}`}
                                                            as={`/${removePrecedingSlash(quickViewData?.link)}`}
                                                            passHref
                                                            onClick={() =>
                                                              setQuickview(
                                                                undefined
                                                              )
                                                            }
                                                          >
                                                            <a
                                                              onClick={() =>
                                                                setQuickview(
                                                                  undefined
                                                                )
                                                              }
                                                              className="h-full"
                                                            >
                                                              <img
                                                                src={
                                                                  generateUri(
                                                                    image?.image,
                                                                    'h=1500'
                                                                  ) ||
                                                                  IMG_PLACEHOLDER
                                                                }
                                                                alt={image.name}
                                                                className="object-cover object-center w-full h-full"
                                                                // sizes="320 600 1000"
                                                                width={600}
                                                                height={1000}
                                                              />
                                                            </a>
                                                          </Link>
                                                        </div>
                                                      ) : (
                                                        <Play className="object-cover object-center w-full h-full" />
                                                      )}
                                                    </div>
                                                  </div>
                                                </SwiperSlide>
                                              )
                                          )}

                                        {!!quickViewData?.videos?.length
                                          ? quickViewData?.videos
                                              ?.slice(0, 1)
                                              ?.map(
                                                (
                                                  video: any,
                                                  videoId: number
                                                ) => (
                                                  <SwiperSlide
                                                    className="px-0"
                                                    key={`${videoId}-slider`}
                                                  >
                                                    <div
                                                      key={videoId}
                                                      className="inline-flex flex-col w-full text-center cursor-pointer lg:w-auto h-full"
                                                    >
                                                      <div className="relative group long-image h-full">
                                                        {/* <div className="image-container h-full"> */}
                                                        {/* <a
                                                            onClick={() =>
                                                              setQuickview(
                                                                undefined
                                                              )
                                                            }
                                                            className="h-full"
                                                          > */}
                                                        <ReactPlayerWrapper
                                                          videoId={videoId}
                                                          isPlaying={false}
                                                          //isPlaying}
                                                          videoUrl={video?.url}
                                                          key={videoId}
                                                          noAllowedAspectRatio={
                                                            true
                                                          }
                                                          heightValue={'617px'}
                                                          customClass={
                                                            'v-height-vid v-quickView'
                                                          }
                                                        />
                                                        {/* </a> */}
                                                        {/* </div> */}
                                                      </div>
                                                    </div>
                                                  </SwiperSlide>
                                                )
                                              )
                                          : null}

                                        {quickViewData?.images
                                          ?.slice(
                                            1,
                                            quickViewData?.images?.length + 1
                                          )
                                          ?.map(
                                            (image: any, idx: number) =>
                                              image?.tag != 'specification' && (
                                                <SwiperSlide
                                                  className="px-0"
                                                  key={`${idx}-slider`}
                                                >
                                                  <div
                                                    key={idx}
                                                    className="inline-flex flex-col w-full text-center cursor-pointer lg:w-auto h-full"
                                                  >
                                                    <div className="relative group long-image h-full">
                                                      {image?.image ? (
                                                        <div className="image-container h-full">
                                                          <Link
                                                            legacyBehavior
                                                            href={`/${removePrecedingSlash(
                                                              quickViewData?.link
                                                            )}`}
                                                            passHref
                                                            onClick={() =>
                                                              setQuickview(
                                                                undefined
                                                              )
                                                            }
                                                          >
                                                            <a
                                                              onClick={() =>
                                                                setQuickview(
                                                                  undefined
                                                                )
                                                              }
                                                              className="h-full"
                                                            >
                                                              <img
                                                                src={
                                                                  generateUri(
                                                                    image?.image,
                                                                    'h=1000&fm=webp'
                                                                  ) ||
                                                                  IMG_PLACEHOLDER
                                                                }
                                                                alt={image.name}
                                                                className="object-cover object-center w-full h-full"
                                                                // sizes="320 600 1000"
                                                                width={600}
                                                                height={1000}
                                                              />
                                                            </a>
                                                          </Link>
                                                        </div>
                                                      ) : (
                                                        <Play className="object-cover object-center w-full h-full" />
                                                      )}
                                                    </div>
                                                  </div>
                                                </SwiperSlide>
                                              )
                                          )}
                                      </div>
                                    </Swiper>
                                  </div>
                                </div>
                                <div className="px-0 sm:col-span-5 sm:mt-10 sm:px-0 ipd-col-5">
                                  <div className="flex-col hidden px-6 sm:flex">
                                    <h2 className="mb-1 text-sm font-normal text-orange-500">
                                      {quickViewData?.classification?.category}
                                    </h2>
                                    <h1 className="grid grid-cols-12 mb-2 text-xl font-bold tracking-tight text-primary sm:text-2xl sm:grid-cols-6">
                                      <div className="col-span-8 sm:col-span-4">
                                        {quickViewData?.name ||
                                          quickViewData?.name}{' '}
                                      </div>
                                      {reviewData?.totalRecord > 0 && (
                                        <div className="col-span-4 pr-2 font-semibold text-right sm:col-span-2 sm:pr-0">
                                          <span className="relative inline-block -top-1">
                                            <img
                                              alt="Star Empty"
                                              src={`${IMAGE_ASSET_BASE_URL}/icons/star-black.svg`}
                                              width={18}
                                              height={18}
                                              style={css}
                                              className="relative top-1"
                                            />
                                          </span>
                                          <Link
                                            legacyBehavior
                                            href="#productRating"
                                            passHref
                                          >
                                            <a className="relative inline-block pl-1 text-sm leading-none -top-1 pl-d-1">
                                              {quickViewData?.rating}
                                              <span className="text-black">
                                                ({reviewData?.totalRecord})
                                              </span>
                                            </a>
                                          </Link>
                                        </div>
                                      )}
                                    </h1>
                                  </div>
                                  <div className="flex justify-between px-6 mt-2">
                                    <div className="item-left">
                                      <p className="mb-2 text-sm font-semibold text-primary sm:text-md">
                                        {/*{priceFormat(
                                          quickViewData?.price?.raw?.withTax
                                        )}*/}
                                        {`${
                                          selectedSizeProduct?.listPrice?.raw
                                            ?.withTax >
                                          selectedSizeProduct?.price?.raw
                                            ?.withTax
                                            ? ''
                                            : 'MRP'
                                        } ${priceFormat(
                                          selectedSizeProduct?.price?.raw
                                            ?.withTax
                                        )}`}

                                        {selectedSizeProduct?.listPrice?.raw
                                          .withTax >
                                        selectedSizeProduct?.price?.raw
                                          ?.withTax ? (
                                          <>
                                            <span className="px-2 text-sm font-normal text-gray-500 sm:text-md">
                                              MRP{' '}
                                              <span className="line-through">
                                                {priceFormat(
                                                  selectedSizeProduct?.listPrice
                                                    ?.raw?.withTax
                                                )}
                                              </span>
                                            </span>
                                            <span className="text-sm font-normal text-green sm:text-md">
                                              {selectedSizeProductDiscount}% off
                                            </span>
                                          </>
                                        ) : null}
                                      </p>
                                      <p className="text-sm pb-2 font-normal text-brown-light sm:text-md">
                                        Inclusive of all taxes
                                      </p>
                                    </div>
                                  </div>

                                  <div
                                    className={`flex-1 w-full  quickview_attributes mobile-padding-quick-view hide-swatch-quickview quick-attr-d mob-slide-icon-hide`}
                                  >
                                    <AttributesHandler
                                      product={quickViewData}
                                      variant={selectedAttrData}
                                      currentVariantData={currentVariantData}
                                      setSelectedAttrData={setSelectedAttrData}
                                      selectedSizeProduct={selectedSizeProduct}
                                      selectedSizeProductDiscount={
                                        selectedSizeProductDiscount
                                      }
                                      onChange={attributeChanged}
                                      isQuickView={true}
                                      setQuickview={setQuickview}
                                      callToAction={buttonConfig}
                                      currentPage={`${
                                        currentPage ?? 'PLP'
                                      } Quick View`}
                                      deviceInfo={deviceInfo}
                                    />
                                  </div>
                                  <div className="sticky bottom-0 z-50 flex py-4 mt-6 bg-white border-t border-gray-200 sm:mt-8 sm:px-6">
                                    <>
                                      <div className="flex w-full px-4 mt-0 sm:mt-0 sm:px-0 sm:shadow-lg">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (!isInWishList) {
                                              handleWishList(quickViewData)
                                            }
                                          }}
                                          className="sm:flex btn-basic-property-big hidden items-center justify-center px-4 sm:w-24 py-3 -mr-0.5 text-black bg-white border-2 border-black quick-wishlist-border rounded-sm hover:bg-red-100 hover:text-orange-600 sm:px-6 hover:border-orange-600"
                                        >
                                          {isInWishList ? (
                                            <i className="sprite-icon sprite-o-whislist"></i>
                                          ) : (
                                            <i className="sprite-icon sprite-product-whislist"></i>
                                          )}
                                          <span className="sr-only">
                                            {BTN_ADD_TO_FAVORITES}
                                          </span>
                                        </button>
                                        <div
                                          className="inline-block sm:hidden"
                                          onClick={() => {
                                            handleViewItemGAEvent()
                                            closeModalAll()
                                          }}
                                        >
                                          <Link
                                            legacyBehavior
                                            href={`/${quickViewData?.link}`}
                                            passHref
                                          >
                                            <a className="sm:hidden btn-basic-property-big flex font-semibold items-center w-24 quick-wishlist-border justify-center px-4 py-3 -mr-0.5 text-black bg-white border-2 border-black rounded-sm">
                                              Details
                                            </a>
                                          </Link>
                                        </div>
                                        <Button
                                          title={buttonConfig.title}
                                          // mobileTitle="Add"
                                          validateAction={
                                            buttonConfig.validateAction
                                          }
                                          section="Quick View"
                                          action={buttonConfig.action}
                                          buttonType={
                                            buttonConfig.type || 'cart'
                                          }
                                        />
                                      </div>
                                      {isEngravingAvailable && (
                                        <button
                                          className="flex items-center justify-center flex-1 max-w-xs px-8 py-3 mt-5 font-medium text-white uppercase bg-gray-400 border border-transparent rounded-sm btn-basic-property-big btn-basic-property hover:bg-pink focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-gray-500 sm:w-full"
                                          onClick={() =>
                                            showEngravingModal(true)
                                          }
                                        >
                                          <span className="font-bold">
                                            {GENERAL_ENGRAVING}
                                          </span>
                                        </button>
                                      )}
                                    </>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )
                      ) : (
                        <>
                          <div className="flex flex-col justify-center w-full py-48 text-center">
                            <h3 className="text-2xl font-semibold text-gray-300">
                              No Data Available for Selected Product
                            </h3>
                          </div>
                        </>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}
